/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { RssFeedItem } from '../types';
import { BlogPost } from '@shared/types';
import { apiCache } from '@shared/services/cacheService';

const RSS_FEEDS = [
    { name: 'Vogue', nameKey: 'rss_source_vogue', url: 'https://www.vogue.com/feed/rss' },
    { name: 'Elle', nameKey: 'rss_source_elle', url: 'https://www.elle.com/rss/all.xml/' },
    { name: 'Harper\'s Bazaar', nameKey: 'rss_source_harpers', url: 'https://www.harpersbazaar.com/rss/all.xml/' },
    { name: 'Fashionista', nameKey: 'rss_source_fashionista', url: 'https://fashionista.com/.rss/full/' },
    { name: 'Business of Fashion', nameKey: 'rss_source_bof', url: 'https://www.businessoffashion.com/feed/' },
    { name: 'Constantina Lifestyle Trends', nameKey: 'rss_source_constantina', url: 'https://diary.constantinalifestyle.one/feed' }
];

const RSS2JSON_API = 'https://api.rss2json.com/v1/api.json';

/**
 * Fetches and parses RSS feeds using the rss2json API.
 * This avoids CORS issues and provides a consistent JSON structure.
 */
export const fetchDynamicRssFeeds = async (): Promise<RssFeedItem[]> => {
    const cacheKey = 'dynamic_rss_feeds';
    const cached = apiCache.get<RssFeedItem[]>(cacheKey);
    if (cached) return cached;

    try {
        const feedPromises = RSS_FEEDS.map(async (feed) => {
            const response = await fetch(`${RSS2JSON_API}?rss_url=${encodeURIComponent(feed.url)}`);
            if (!response.ok) throw new Error(`Failed to fetch ${feed.name}`);
            const data = await response.json();

            if (data.status !== 'ok') return [];

            return data.items.map((item: any) => ({
                id: item.guid || item.link,
                title: item.title,
                source: feed.name,
                sourceKey: feed.nameKey,
                link: item.link,
                pubDate: item.pubDate ? new Date(item.pubDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                snippet: item.content || item.description || '',
                imageUrl: item.thumbnail || item.enclosure?.link || ''
            }));
        });

        const results = await Promise.all(feedPromises);
        // Flatten and sort by date descending
        const data = results.flat().sort((a, b) => new Date(b.pubDate).getTime() - new Date(a.pubDate).getTime());
        apiCache.set(cacheKey, data, 30 * 60 * 1000); // 30 mins
        return data;
    } catch (error) {
        console.error('Error fetching RSS feeds:', error);
        return [];
    }
};

const DIARY_FEED_URL = 'https://diary.constantinalifestyle.one/feed';

/**
 * Fetches the last 5 articles from the Constantina Lifestyle diary
 * and converts them into BlogPost-compatible objects.
 */
export const fetchDiaryPosts = async (): Promise<BlogPost[]> => {
    const cacheKey = 'diary_blog_posts';
    const cached = apiCache.get<BlogPost[]>(cacheKey);
    if (cached) return cached;

    try {
        const response = await fetch(`${RSS2JSON_API}?rss_url=${encodeURIComponent(DIARY_FEED_URL)}&count=5`);
        if (!response.ok) throw new Error('Failed to fetch diary feed');
        const data = await response.json();

        if (data.status !== 'ok' || !data.items) return [];

        const posts = data.items.slice(0, 5).map((item: any, index: number) => {
            // Strip HTML tags from description for a clean excerpt
            const rawExcerpt = (item.description || item.content || '')
                .replace(/<[^>]*>?/gm, '')
                .substring(0, 200);

            return {
                id: `diary-${index}-${Date.now()}`,
                titleKey: '', // not used for external posts
                excerptKey: '', // not used for external posts
                title: item.title || 'Untitled',
                excerpt: rawExcerpt ? `${rawExcerpt}…` : '',
                imageUrl: item.thumbnail || item.enclosure?.link || '',
                date: item.pubDate ? new Date(item.pubDate).toISOString().split('T')[0] : new Date().toISOString().split('T')[0],
                isExternal: true,
                externalUrl: item.link,
                authorId: undefined,
            } as BlogPost;
        });

        apiCache.set(cacheKey, posts, 30 * 60 * 1000); // 30 mins
        return posts;
    } catch (error) {
        console.error('Error fetching diary posts:', error);
        return [];
    }
};
